import React, { useState, useEffect } from 'react';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import {
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { FaGripVertical, FaSave, FaTimes } from 'react-icons/fa';
import { logger } from '../utils/logger';

// Componente per gli elementi trascinabili
const SortableItem = ({ 
  item, 
  index, 
  sequenceField, 
  displayField, 
  descriptionField, 
  themeColor,
  showOriginalSequence = true
}) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: item.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`flex items-center p-4 bg-white dark:bg-gray-800 border rounded-lg shadow-sm transition-all duration-200 ${
        isDragging
          ? `border-${themeColor}-500 shadow-lg bg-${themeColor}-50 dark:bg-${themeColor}-900/20`
          : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600'
      }`}
    >
      {/* Drag Handle */}
      <div
        {...attributes}
        {...listeners}
        className={`mr-4 p-2 text-gray-400 hover:text-${themeColor}-500 cursor-grab active:cursor-grabbing transition-colors`}
      >
        <FaGripVertical className="w-4 h-4" />
      </div>

      {/* Sequence Number */}
      <div className="flex-shrink-0 w-20 text-center">
        <span className={`inline-block px-2 py-1 text-xs font-mono rounded-full bg-gray-200 text-gray-800 dark:bg-gray-700 dark:text-gray-200`}>
          {showOriginalSequence ? item[sequenceField] : index + 1}
        </span>
      </div>

      {/* Content */}
      <div className="flex-1 ml-4">
        <div className="font-medium text-gray-900 dark:text-gray-100">
          {item[displayField]}
        </div>
        {descriptionField && item[descriptionField] && (
          <div className="text-sm text-gray-500 dark:text-gray-400 mt-1">
            {item[descriptionField]}
          </div>
        )}
      </div>

      {/* Position Indicator */}
      <div className="flex-shrink-0 text-sm text-gray-500 dark:text-gray-400 flex flex-col items-end">
        <div>Pos. {index + 1}</div>
        {showOriginalSequence && item[sequenceField] !== (index + 1) && (
          <div className="text-xs text-orange-500 dark:text-orange-400">
            (era {item[sequenceField]})
          </div>
        )}
      </div>
    </div>
  );
};

const DragDropSequenceManager = ({
  isOpen,
  onClose,
  items = [],
  title = "Gestione Ordinamento",
  sequenceField = "sequence", // Campo per la sequenza (es. CAM_Sequenza, ATT_Sequenza)
  idField = "id", // Campo ID (es. CAM_Id_Campi, ATT_Id_Attributi)
  displayField = "name", // Campo da visualizzare (es. CAM_Nome, ATT_Nome)
  descriptionField = "description", // Campo descrizione opzionale
  onSave, // Callback per salvare le modifiche
  themeColor = 'emerald',
  minSequence = 1, // Sequenza minima per il filtraggio
  maxSequence = 999, // Sequenza massima per il filtraggio
  sequenceStep = 1, // Non più usato per il calcolo, ma mantenuto per compatibilità
}) => {
  const [orderedItems, setOrderedItems] = useState([]);
  const [originalItems, setOriginalItems] = useState([]);
  const [hasChanges, setHasChanges] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  // Filtra e ordina gli elementi modificabili (sequenza tra minSequence e maxSequence)
  useEffect(() => {
    if (!items || items.length === 0) {
      setOrderedItems([]);
      setOriginalItems([]);
      return;
    }

    const editableItems = items.filter(item => {
      const sequence = Number(item[sequenceField]) || 0;
      return sequence >= minSequence && sequence <= maxSequence;
    });

    // Ordina per sequenza corrente e aggiungi un ID unico per dnd-kit
    const sorted = [...editableItems]
      .sort((a, b) => {
        const aSeq = Number(a[sequenceField]) || 0;
        const bSeq = Number(b[sequenceField]) || 0;
        return aSeq - bSeq;
      })
      .map(item => ({
        ...item,
        id: String(item[idField]) // dnd-kit richiede un ID stringa
      }));

    setOrderedItems(sorted);
    setOriginalItems([...sorted]); // Mantieni una copia dell'ordine originale
    setHasChanges(false);
  }, [items, sequenceField, idField, minSequence, maxSequence]);

  const handleDragEnd = (event) => {
    const { active, over } = event;

    if (over && active.id !== over.id) {
      setOrderedItems((currentItems) => {
        const oldIndex = currentItems.findIndex((item) => item.id === active.id);
        const newIndex = currentItems.findIndex((item) => item.id === over.id);
        
        const newArray = arrayMove(currentItems, oldIndex, newIndex);
        
        setHasChanges(true); // Set that changes have been made
        return newArray;
      });
    }
  };

  const handleSave = async () => {
    setIsSaving(true);
    logger.info(`[DragDropSequenceManager] Saving sequence for ${orderedItems.length} items.`);

    // Create a new array with updated sequence numbers
    const itemsToUpdate = orderedItems.map((item, index) => ({
      ...item,
      [sequenceField]: index + minSequence, // Recalculate sequence
    }));

    // Filter only the items that have actually changed sequence
    const changedItems = itemsToUpdate.filter(updatedItem => {
      const originalItem = originalItems.find(o => o[idField] === updatedItem[idField]);
      return originalItem && originalItem[sequenceField] !== updatedItem[sequenceField];
    });

    if (changedItems.length === 0) {
      logger.info('[DragDropSequenceManager] No sequence changes detected.');
      onClose();
      return;
    }
    
    // FIX: Create a specific payload with only the ID and the new sequence value.
    // This ensures that we don't pass along any extra data from the component's internal state.
    const payload = changedItems.map(item => ({
      [idField]: item[idField],
      [sequenceField]: item[sequenceField],
    }));

    logger.debug('[DragDropSequenceManager] Payload for sequence update:', payload);

    await onSave(payload); // Call the onSave callback with the explicit payload

    setIsSaving(false);
    onClose(); // Close modal after saving
  };

  const handleCancel = () => {
    onClose();
  };

  const handleReset = () => {
    if (hasChanges) {
      setOrderedItems([...originalItems]);
      setHasChanges(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-60 backdrop-blur-sm p-4">
      <div className={`bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-4xl max-h-[90vh] flex flex-col border-2 border-${themeColor}-500`}>
        {/* Header */}
        <div className={`flex justify-between items-center p-4 border-b border-gray-300 dark:border-gray-700 bg-gray-100 dark:bg-gray-800 rounded-t-lg`}>
          <h3 className={`text-lg font-semibold text-gray-800 dark:text-gray-200`}>
            {title}
          </h3>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-4">
          {orderedItems.length === 0 ? (
            <div className="text-center py-8">
              <div className="text-gray-500 dark:text-gray-400">
                <p className="text-lg mb-2">Nessun elemento ordinabile trovato</p>
                <p className="text-sm">
                  Gli elementi con sequenza tra {minSequence} e {maxSequence} possono essere riordinati
                </p>
              </div>
            </div>
          ) : (
            <DndContext
              sensors={sensors}
              collisionDetection={closestCenter}
              onDragEnd={handleDragEnd}
            >
              <SortableContext
                items={orderedItems.map(item => item.id)}
                strategy={verticalListSortingStrategy}
              >
                <div className="space-y-2">
                  {orderedItems.map((item, index) => (
                    <SortableItem
                      key={item.id}
                      item={item}
                      index={index}
                      sequenceField={sequenceField}
                      displayField={displayField}
                      descriptionField={descriptionField}
                      themeColor={themeColor}
                      showOriginalSequence={!hasChanges}
                    />
                  ))}
                </div>
              </SortableContext>
            </DndContext>
          )}
        </div>

        {/* Footer */}
        <div className="p-4 border-t border-gray-300 dark:border-gray-700 flex justify-between items-center bg-gray-50 dark:bg-gray-900/50">
          <div className="text-sm text-gray-600 dark:text-gray-400">
            {hasChanges ? (
              <span className={`text-${themeColor}-600 dark:text-${themeColor}-400 font-medium`}>
                • Modifiche non salvate - Le sequenze saranno riordinate da 1 a {orderedItems.length}
              </span>
            ) : (
              <span>Trascina gli elementi per modificare l'ordine</span>
            )}
          </div>
          <div className="flex space-x-3">
            {hasChanges && (
              <button
                onClick={handleReset}
                disabled={isSaving}
                className="px-4 py-2 text-sm font-medium border border-gray-300 dark:border-gray-600 rounded-lg text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition-all duration-200 disabled:opacity-50"
              >
                Ripristina
              </button> 
            )}
            <button
              onClick={handleCancel}
              disabled={isSaving}
              className="px-6 py-3 text-sm font-medium border-2 border-gray-400 dark:border-gray-500 rounded-lg text-gray-800 dark:text-gray-200 hover:bg-gray-200 dark:hover:bg-gray-600 transition-all duration-200 disabled:opacity-50"
            >
              Annulla
            </button>
            {hasChanges && (
              <button
                onClick={handleSave}
                disabled={isSaving}
                className={`px-6 py-3 text-sm font-medium bg-${themeColor}-600 text-white rounded-lg hover:bg-${themeColor}-700 transition-all duration-200 disabled:opacity-50 flex items-center space-x-2 shadow-lg`}
              >
                {isSaving ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                    <span>Salvando...</span>
                  </>
                ) : (
                  <>
                    <FaSave className="w-4 h-4" />
                    <span>Salva Ordinamento</span>
                  </>
                )}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default DragDropSequenceManager;